import { NavLink } from "react-router";
import type { ApprovalBlocker, AuditCategory, DualControlActionType, DualControlStatus, OrgRole } from "../../../api/types";
import type { Tone } from "../../../components/ui";

// Wording for the organization screens (UX-DESIGN.md §4.2-§4.5). The
// machine values come from migration 0062; the portal owns the words.

/** Display order: _org_role_rank, 0062_organization.up.sql:216-220. */
export const ROLES: OrgRole[] = ["owner", "admin", "payout_approver", "spend_approver", "editor", "member"];

export const ROLE_LABEL: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  payout_approver: "Payout approver",
  spend_approver: "Spend approver",
  editor: "Editor",
  member: "Member",
};

/**
 * One line per role, from UX-DESIGN.md §4.2's matrix: `member` is the
 * matrix's "ordinary user", `editor` its "campaign/slot editor", `admin`
 * its "member/key admin"; `owner` is admin plus the only role that can
 * grant or remove owner (0062 header, point 1).
 */
export const ROLE_DESCRIPTION: Record<OrgRole, string> = {
  owner: "Everything an admin can do, and the only role that can grant or remove owner or transfer ownership.",
  admin: "Invites and removes members, changes roles, creates and revokes API keys, and sees the full audit log.",
  payout_approver: "Requests and approves payouts and refunds, submits disputes, and sees the financial part of the audit log.",
  spend_approver: "Approves spend and budget increases, edits campaigns, and sees the financial part of the audit log.",
  editor: "Creates and edits draft campaigns and slots, and submits disputes.",
  member: "Views campaigns, sites and reporting, and their own activity in the audit log.",
};

/** _org_role_privileged, 0062:211-213: granting these goes through dual control. */
export function isPrivileged(role: OrgRole): boolean {
  return role === "owner" || role === "admin" || role === "payout_approver" || role === "spend_approver";
}

/** Owner or admin: may manage members (_org_require_manager, 0062:242-255). */
export function isManager(roles: readonly string[]): boolean {
  return roles.includes("owner") || roles.includes("admin");
}

/** _audit_viewer_tier, 0062:385-397. */
export type AuditTier = "full" | "financial" | "own";
export function auditTier(roles: readonly string[]): AuditTier {
  if (roles.includes("owner") || roles.includes("admin")) return "full";
  if (roles.includes("spend_approver") || roles.includes("payout_approver")) return "financial";
  return "own";
}

export const AUDIT_TIER_TEXT: Record<AuditTier, string> = {
  full: "As an owner or admin you see every event in this organization's log.",
  financial:
    "As a spend or payout approver you see the financial and dispute events for the whole organization, plus your own activity. Other members' sign-ins, security and member changes stay visible to owners and admins only.",
  own: "Your role shows only your own activity: things you did, and changes made to your account. Owners and admins see the whole log; spend and payout approvers see its financial and dispute events.",
};

export const AUDIT_CATEGORY_LABEL: Record<AuditCategory, string> = {
  security: "Security",
  member: "Members",
  api_key: "API keys",
  financial: "Financial",
  dispute: "Disputes",
};

/** "member_role_change_requested" → "Member role change requested". Actions are stable machine values (0062:147). */
export function humanize(code: string): string {
  const s = code.replace(/[._]/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** _dc_title, 0062:639-647. */
export const ACTION_TYPE_TITLE: Record<DualControlActionType, string> = {
  payout_destination_change: "Payout destination change",
  reconciliation_override: "Reconciliation override",
  member_role_grant: "Role change",
  ownership_transfer: "Ownership transfer",
};

export const DC_STATUS: Record<DualControlStatus, { label: string; tone: Tone }> = {
  pending: { label: "Waiting for approval", tone: "warning" },
  approved: { label: "Approved, waiting period running", tone: "info" },
  executed: { label: "Took effect", tone: "success" },
  canceled: { label: "Canceled", tone: "neutral" },
  expired: { label: "Expired", tone: "neutral" },
  rejected: { label: "Did not go ahead", tone: "error" },
};

/** Why you can't approve (list_dual_control_requests approval_blocker, 0062:1742). */
export function blockerText(b: ApprovalBlocker): string {
  switch (b) {
    case "approver_is_requester":
      return "You made this request, so a different member has to approve it.";
    case "approver_is_subject":
      return "This request is about you, so a different member has to approve it.";
    case "insufficient_authority":
      return "Your role doesn't give you authority over this kind of request.";
    case "authority_not_yet_active":
      return "Your authority is still in its 72-hour waiting period after being granted.";
  }
}

/** UX-DESIGN.md §4.2's required note for the single-member fallback. */
export const SINGLE_USER_FALLBACK_TEXT =
  "This organization has nobody else who could approve this, so extended verification is used instead of a second approver: a 7-day waiting period, with a notice sent to the requester's recovery contact.";

/** The dual-control + tenure explanation shown before granting a privileged role (§4.2, §4.3). */
export const PRIVILEGED_ROLE_TEXT =
  "This is a privileged role, so it needs a second approver: another owner or admin confirms it, and it takes effect no earlier than 24 hours later (7 days if nobody else can approve, with a notice to the recovery contact). Once granted, the new authority can't be used to approve anyone else's request for 72 hours. You'll confirm it's you first.";

export function orgBase(home: string): string {
  return `${home}/org`;
}

/** Sub-navigation shared by the organization screens. */
export function OrgTabs({ home }: { home: string }) {
  const tabs = [
    { to: `${home}/organization`, label: "Profile", end: true },
    { to: `${home}/org/members`, label: "Members" },
    { to: `${home}/org/approvals`, label: "Pending approvals" },
    { to: `${home}/org/audit`, label: "Audit log" },
    { to: `${home}/org/notifications`, label: "Notifications" },
  ];
  return (
    <nav aria-label="Organization" className="tabs">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className="tab">
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** "in 3 h" style is avoided on purpose: an absolute local time can't drift while the page is open. */
export function isFuture(iso: string | null | undefined, now: number = Date.now()): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return !Number.isNaN(t) && t > now;
}
